/**
 * 관리자 대시보드
 *
 *  구성 3계층 — 위에서 아래로 "해야 할 일 → 오늘의 흐름 → 최근 추이"
 *   ① 처리 대기(액션 큐) : 관리자가 지금 눌러서 처리해야 하는 건수. 0 이면 회색.
 *   ② 오늘의 운영 지표   : 서비스가 정상 규모로 돌고 있는지 + 전일 대비 증감.
 *   ③ 추이·분포         : 최근 14일 흐름과 당일 예약 상태 구성.
 *
 *  화면 이동은 사이드바가 담당한다 — 여기에 링크 모음을 두면 사이드바와 중복된다.
 *
 *  ⚠ 이 화면에는 하드코딩된 수치를 절대 두지 않는다. 조회 실패는 실패로 표시한다 —
 *    "0 건" 으로 위장하면 관리자가 처리할 게 없다고 오판한다.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Calculator,
  CalendarDays,
  Wallet,
  MessageSquareWarning,
  Send,
  Store,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  type LucideIcon,
} from 'lucide-react';
import {
  dashboardApi,
  RESERV_STATUS_LABEL,
  type DashboardAlerts,
  type DashboardSummary,
  type TrendPoint,
} from '@/services/api/dashboard';
import {
  PageHeader,
  Section,
  LoadingState,
  ErrorState,
} from '@/components/common';

/* ─────────────────────────────────────────────────────────────────
 *  포맷 헬퍼
 * ───────────────────────────────────────────────────────────────── */

const num = (n: number | undefined | null) => (n ?? 0).toLocaleString();
const krw = (n: number | undefined | null) => `₩${(n ?? 0).toLocaleString()}`;

const timeText = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** MM/DD */
const shortDate = (ymd: string) => ymd.slice(5).replace('-', '/');

/**
 * apiClient 는 Error 가 아니라 `{ message, status, code }` 평범한 객체로 reject 한다.
 * ErrorState 가 읽을 수 있는 문자열로 정규화 — 상태 코드까지 남겨 CS 문의 시 추적 가능하게 한다.
 */
const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const e = error as { message?: string; status?: number };
    if (e.message) return e.status ? `${e.message} (HTTP ${e.status})` : e.message;
  }
  return '알 수 없는 오류가 발생했습니다.';
};

/* ─────────────────────────────────────────────────────────────────
 *  ① 처리 대기 카드
 * ───────────────────────────────────────────────────────────────── */

interface AlertCardProps {
  to: string;
  label: string;
  count: number;
  icon: LucideIcon;
  /** 카드 하단 보조 문구 (금액·임계값 등). 없어도 자리는 항상 확보한다. */
  sub?: string;
}

/**
 * 액션 큐 카드.
 *  - 건수가 0 이면 의도적으로 채도를 낮춘다 — 색이 남아 있으면 "처리할 게 있다"로 읽힌다.
 *  - 모든 카드가 같은 높이·같은 줄 구성(라벨 / 숫자 / 보조문구)을 갖는다.
 *    보조 문구가 없는 카드도 빈 줄을 남겨 카드마다 높이가 들쭉날쭉해지지 않게 한다.
 */
function AlertCard({ to, label, count, icon: Icon, sub }: AlertCardProps) {
  const active = count > 0;
  return (
    <Link
      to={to}
      className={`flex h-full items-center gap-3 rounded-lg border p-3 transition-colors ${
        active
          ? 'border-amber-300 bg-amber-50 hover:border-amber-400 hover:bg-amber-100'
          : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      <span
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
          active ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'
        }`}
      >
        <Icon size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs leading-5 text-gray-600">
          {label}
        </span>
        <span
          className={`block text-lg font-bold leading-6 tabular-nums ${
            active ? 'text-amber-800' : 'text-gray-400'
          }`}
        >
          {num(count)}
        </span>
        <span className="block h-4 truncate text-2xs leading-4 text-gray-500">
          {sub ?? ''}
        </span>
      </span>
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  ② KPI 카드
 * ───────────────────────────────────────────────────────────────── */

interface KpiCardProps {
  label: string;
  value: string;
  sub?: string;
  /** 전일 대비 증감 (없으면 표시하지 않음). money 면 증감도 원화로 표기. */
  delta?: { current: number; previous: number; money?: boolean };
  accent?: 'blue' | 'emerald' | 'violet' | 'slate';
}

const ACCENT_BG: Record<string, string> = {
  blue: 'bg-blue-50 text-blue-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  violet: 'bg-violet-50 text-violet-700',
  slate: 'bg-gray-100 text-gray-600',
};

function DeltaBadge({
  current,
  previous,
  money,
}: {
  current: number;
  previous: number;
  money?: boolean;
}) {
  const diff = current - previous;
  if (diff === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-2xs text-gray-400">
        <Minus size={12} /> 전일 동일
      </span>
    );
  }
  const up = diff > 0;
  // 증감 자체는 좋고 나쁨을 판단하지 않는다 — 방향만 색으로 구분한다.
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-2xs ${
        up ? 'text-emerald-600' : 'text-rose-600'
      }`}
    >
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {up ? '+' : '-'}
      {money ? krw(Math.abs(diff)) : num(Math.abs(diff))}{' '}
      <span className="text-gray-400">전일 대비</span>
    </span>
  );
}

function KpiCard({ label, value, sub, delta, accent = 'slate' }: KpiCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        <span
          className={`rounded px-1.5 py-0.5 text-3xs font-medium ${ACCENT_BG[accent]}`}
        >
          오늘
        </span>
      </div>
      <div className="mt-1.5 text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="mt-0.5 truncate text-xs text-gray-500">{sub}</div>}
      {delta && (
        <div className="mt-1">
          <DeltaBadge
            current={delta.current}
            previous={delta.previous}
            money={delta.money}
          />
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  ③ 추이 미니 차트 (라이브러리 없이 div 막대)
 * ───────────────────────────────────────────────────────────────── */

interface MiniBarsProps {
  points: TrendPoint[];
  /** 막대 높이를 결정할 값 선택자 */
  pick: (p: TrendPoint) => number;
  /** 툴팁 문구 */
  format: (p: TrendPoint) => string;
  colorClass: string;
  /**
   * 이 날짜(YYYY-MM-DD)보다 뒤는 "예정" 구간으로 옅게 그리고 경계선을 넣는다.
   * 지정하지 않으면 전부 확정 구간으로 본다.
   */
  todayDate?: string;
}

function MiniBars({ points, pick, format, colorClass, todayDate }: MiniBarsProps) {
  const max = Math.max(1, ...points.map(pick));
  const firstFutureIdx = todayDate
    ? points.findIndex(p => p.date > todayDate)
    : -1;
  return (
    <div>
      <div className="flex h-28 items-end gap-1">
        {points.map((p, i) => {
          const v = pick(p);
          // 값이 0 이 아니면 최소 2% 는 그려서 "데이터 없음"과 구분되게 한다.
          const pct = v === 0 ? 0 : Math.max(2, Math.round((v / max) * 100));
          const isFuture = firstFutureIdx >= 0 && i >= firstFutureIdx;
          return (
            <div
              key={p.date}
              // h-full 필수 — 부모가 items-end 라 컬럼이 늘어나지 않으면 높이가 auto 가 되고,
              // 그 안의 막대 height:% 가 기준을 잃어 0 으로 계산된다(막대가 아예 안 보임).
              className={`flex h-full flex-1 flex-col justify-end ${
                i === firstFutureIdx ? 'border-l border-dashed border-gray-300 pl-0.5' : ''
              }`}
              title={`${p.date}${isFuture ? ' (예정)' : ''} · ${format(p)}`}
            >
              <div
                className={`w-full rounded-t ${
                  v === 0 ? 'bg-gray-100' : isFuture ? `${colorClass} opacity-40` : colorClass
                }`}
                style={{ height: v === 0 ? '2px' : `${pct}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-3xs text-gray-400">
        <span>{points.length > 0 ? shortDate(points[0].date) : ''}</span>
        <span>
          {points.length > 0 ? shortDate(points[points.length - 1].date) : ''}
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  페이지
 * ───────────────────────────────────────────────────────────────── */

export function DashboardPage() {
  const alertsQuery = useQuery({
    queryKey: ['dashboard', 'alerts'],
    queryFn: () => dashboardApi.getAlerts(),
    // 처리 대기 건수는 자주 바뀐다 — 다른 화면에서 돌아오면 다시 읽는다.
    staleTime: 30 * 1000,
    // 전역 기본값(3회 재시도)은 첫 화면에서 실패가 7초 뒤에야 보인다. 대시보드는 빨리 실패를 알린다.
    retry: 1,
  });

  const summaryQuery = useQuery({
    queryKey: ['dashboard', 'summary', 14],
    queryFn: () => dashboardApi.getSummary({ trendDays: 14 }),
    staleTime: 60 * 1000,
    retry: 1,
  });

  const alerts: DashboardAlerts | undefined = alertsQuery.data;
  const summary: DashboardSummary | undefined = summaryQuery.data;

  const refreshAll = () => {
    alertsQuery.refetch();
    summaryQuery.refetch();
  };

  /** 상태별 분포 — 취소는 맨 뒤로 밀어 시각적 비중을 낮춘다. */
  const statusRows = useMemo(() => {
    const rows = summary?.reservationByStatus ?? [];
    const total = rows.reduce((acc, r) => acc + r.count, 0) || 1;
    return [...rows]
      .sort((a, b) => {
        const aCancel = a.status === 'RESERV_STATUS_CANCELED' ? 1 : 0;
        const bCancel = b.status === 'RESERV_STATUS_CANCELED' ? 1 : 0;
        if (aCancel !== bCancel) return aCancel - bCancel;
        return b.count - a.count;
      })
      .map(r => ({ ...r, pct: Math.round((r.count / total) * 100) }));
  }, [summary]);

  const attendRate =
    summary && summary.attendExpected > 0
      ? Math.round((summary.attendChecked / summary.attendExpected) * 100)
      : 0;

  const baseAt = alerts?.baseAt ?? summary?.baseAt;

  return (
    <div className="space-y-4">
      <PageHeader
        title="대시보드"
        subtitle={
          baseAt
            ? `기준 ${timeText(baseAt)} · 멍투데이 운영 현황`
            : '멍투데이 운영 현황'
        }
        right={
          <div className="flex justify-end">
            <button
              type="button"
              onClick={refreshAll}
              disabled={alertsQuery.isFetching || summaryQuery.isFetching}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                size={14}
                className={
                  alertsQuery.isFetching || summaryQuery.isFetching
                    ? 'animate-spin'
                    : ''
                }
              />
              새로고침
            </button>
          </div>
        }
      />

      {/* ① 처리 대기 */}
      <Section
        title="처리 대기"
        description="숫자를 누르면 해당 관리 화면으로 이동합니다."
      >
        {alertsQuery.error ? (
          <ErrorState
            error={toErrorMessage(alertsQuery.error)}
            onRetry={() => alertsQuery.refetch()}
          />
        ) : !alerts ? (
          /* 데이터가 없으면 재시도 대기 중이라도 로딩으로 둔다 —
             값을 0 으로 그리면 "처리할 게 없다"는 잘못된 신호가 된다. */
          <LoadingState message="처리 대기 건수를 불러오는 중..." />
        ) : (
          <ul className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-6">
            <li>
              <AlertCard
                to="/business"
                label="매장 활성화 요청"
                count={alerts?.companyActivationRequested ?? 0}
                icon={Store}
              />
            </li>
            <li>
              <AlertCard
                to="/daily-reservations"
                label="예약 승인 대기"
                count={alerts?.reservationPending ?? 0}
                icon={CalendarDays}
                // 기한 지난 미처리 건은 승인해도 효력이 없어 메인 숫자에서 제외한다.
                // 보조줄 노출은 보류 — 필요해지면 아래 주석을 풀면 된다.
                // (BE 는 alerts.reservationPendingOverdue 로 계속 내려주고 있다)
                // sub={
                //   alerts?.reservationPendingOverdue
                //     ? `지난 미처리 ${num(alerts.reservationPendingOverdue)}건`
                //     : undefined
                // }
              />
            </li>
            <li>
              <AlertCard
                to="/settlement"
                label="미지급 정산"
                count={alerts?.settlementUnpaidCount ?? 0}
                icon={Calculator}
                sub={krw(alerts?.settlementUnpaidAmount)}
              />
            </li>
            <li>
              <AlertCard
                to="/credit/companies"
                label="크레딧 잔액 부족"
                count={alerts?.creditLowBalanceCount ?? 0}
                icon={Wallet}
                sub={`${num(alerts?.creditLowBalanceThreshold)} 미만`}
              />
            </li>
            <li>
              <AlertCard
                to="/kakao-alim/logs"
                label="알림톡 실패 (24h)"
                count={alerts?.kakaoAlimFailed24h ?? 0}
                icon={MessageSquareWarning}
              />
            </li>
            <li>
              <AlertCard
                to="/push-messages"
                label="Push 발송 대기"
                count={alerts?.pushScheduled ?? 0}
                icon={Send}
              />
            </li>
          </ul>
        )}
      </Section>

      {/* ② 오늘의 운영 지표 */}
      {summaryQuery.error ? (
        <ErrorState
          error={toErrorMessage(summaryQuery.error)}
          onRetry={() => summaryQuery.refetch()}
        />
      ) : !summary ? (
        <LoadingState message="운영 지표를 불러오는 중..." />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              label="오늘 예약"
              value={`${num(summary?.reservationTotal)}건`}
              sub="취소 제외 · 진행 중인 예약 기준"
              delta={{
                current: summary?.reservationTotal ?? 0,
                previous: summary?.reservationTotalPrevDay ?? 0,
              }}
              accent="blue"
            />
            <KpiCard
              label="등원 진행률"
              value={`${attendRate}%`}
              sub={`등원 ${num(summary?.attendChecked)} / 예정 ${num(
                summary?.attendExpected,
              )} · 하원 ${num(summary?.dismissChecked)}`}
              accent="blue"
            />
            <KpiCard
              label="이용권 결제 (거래액)"
              value={krw(summary?.ticketPaidAmount)}
              sub={`온라인 ${num(summary?.ticketPaidCount)}건 · 보호자 → 매장`}
              delta={{
                current: summary?.ticketPaidAmount ?? 0,
                previous: summary?.ticketPaidAmountPrevDay ?? 0,
                money: true,
              }}
              accent="emerald"
            />
            <KpiCard
              label="크레딧 충전 (자사 수익)"
              value={krw(summary?.creditChargedAmount)}
              sub={`${num(summary?.creditChargedCount)}건 · 사업자 → 멍투데이`}
              accent="violet"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <KpiCard
              label="크레딧 사용량"
              value={num(summary?.creditUsed)}
              sub="USE_* 차감 합계"
              accent="violet"
            />
            <KpiCard
              label="신규 보호자"
              value={`${num(summary?.newGuardians)}명`}
              sub={`전체 ${num(summary?.totalGuardians)}명`}
              accent="slate"
            />
            <KpiCard
              label="신규 사업자"
              value={`${num(summary?.newBusinessUsers)}명`}
              sub="원장·선생님 계정"
              accent="slate"
            />
            <KpiCard
              label="활성 매장"
              value={`${num(summary?.activeCompanies)}곳`}
              sub="가입 매장 기준 (미가입 업체 제외)"
              accent="slate"
            />
          </div>

          {/* ③ 추이 + 상태 분포 */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Section
              title="예약 건수 추이"
              description="과거 7일 + 예정 7일 · 이용일 기준, 취소 제외"
              className="lg:col-span-1"
            >
              <MiniBars
                points={summary?.trend ?? []}
                pick={p => p.reservationCount}
                format={p => `${num(p.reservationCount)}건`}
                colorClass="bg-blue-400"
                todayDate={summary?.baseDate}
              />
            </Section>

            <Section
              title="이용권 결제액 추이"
              description="최근 14일 · 보호자 온라인 결제만 (수동발급 제외)"
              className="lg:col-span-1"
            >
              {/* 결제는 미래가 없으므로 예정 구간(기준일 이후)은 잘라내고 그린다 */}
              <MiniBars
                points={(summary?.trend ?? []).filter(
                  p => !summary?.baseDate || p.date <= summary.baseDate,
                )}
                pick={p => p.ticketPaidAmount}
                format={p => krw(p.ticketPaidAmount)}
                colorClass="bg-emerald-400"
              />
            </Section>

            <Section
              title="오늘 예약 상태 분포"
              description="진행 중인 예약의 상태별 구성"
              className="lg:col-span-1"
            >
              {statusRows.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">
                  오늘 진행되는 예약이 없습니다.
                </p>
              ) : (
                <ul className="space-y-2">
                  {statusRows.map(r => (
                    <li key={r.status}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-700">
                          {RESERV_STATUS_LABEL[r.status] ?? r.status}
                        </span>
                        <span className="font-medium text-gray-900">
                          {num(r.count)}건
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100">
                        <div
                          className={`h-1.5 rounded-full ${
                            r.status === 'RESERV_STATUS_CANCELED'
                              ? 'bg-gray-300'
                              : 'bg-primary-400'
                          }`}
                          style={{ width: `${r.pct}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
